import React, { useMemo, useState, useEffect } from "react";
import { PermissionAction, ResourceType } from "@/types/permissions";
import { useParams } from "react-router-dom";
import { Shield } from "lucide-react";
import { usePermissions } from "@/contexts/PermissionContext";
import { useAuthStore } from "@/stores/auth/auth-store";
import { Project } from "@/types";
import { WARNING } from "@/styles/tokens";
import {
  getViewerPermissionIdString,
  getViewerPermissionIdStrings,
} from "@/components/permissions/viewerPermissionIds";

interface PermissionAwareEditorToolbarProps {
  project?: Project | null;
  onOpenPermissionMatrix?: () => void;
  onOpenProjectSettings?: () => void;
  onOpenSharing?: () => void;
  onToggleVersioning?: () => void;
  isVersioningOpen?: boolean;
  isVersioningMode?: boolean;
}

const getProjectOwnerId = (project: Project | null): string | undefined => {
  return (
    getViewerPermissionIdString(project?.ownerId) ??
    getViewerPermissionIdString(project?.owner_id)
  );
};

export const PermissionAwareEditorToolbar: React.FC<
  PermissionAwareEditorToolbarProps
> = ({
  project,
  onOpenPermissionMatrix,
  onOpenProjectSettings,
  onOpenSharing,
  onToggleVersioning,
  isVersioningOpen,
  isVersioningMode,
}) => {
  const { projectId } = useParams<{ projectId: string }>();
  const { checkPermission } = usePermissions();
  const currentUser = useAuthStore((state) => state.user);
  const currentUserIds = useMemo(
    () => getViewerPermissionIdStrings(currentUser?.id, currentUser?._id),
    [currentUser?.id, currentUser?._id],
  );

  const [permissions, setPermissions] = useState({
    canEdit: false,
    canSave: false,
    canShare: false,
    canManagePermissions: false,
    canManageSettings: false,
  });

  // Check permissions for various actions
  useEffect(() => {
    if (!projectId || !project) return; // Don't check permissions until project data is loaded

    const checkProjectPermissions = async () => {
      const ownerId = getProjectOwnerId(project);

      if (ownerId && currentUserIds.has(ownerId)) {
        setPermissions({
          canEdit: true,
          canSave: true,
          canShare: true,
          canManagePermissions: true,
          canManageSettings: true,
        });
        return;
      }

      try {
        const [editResult, shareResult, adminResult] = await Promise.all([
          checkPermission({
            resource: {
              type: ResourceType.PROJECT,
              id: projectId,
              ownerId: getProjectOwnerId(project),
            },
            action: PermissionAction.UPDATE,
          }),
          checkPermission({
            resource: {
              type: ResourceType.PROJECT,
              id: projectId,
              ownerId: getProjectOwnerId(project),
            },
            action: PermissionAction.SHARE,
          }),
          checkPermission({
            resource: {
              type: ResourceType.PROJECT,
              id: projectId,
              ownerId: getProjectOwnerId(project),
            },
            action: PermissionAction.MANAGE_PERMISSIONS,
          }),
        ]);

        setPermissions({
          canEdit: editResult.allowed,
          canSave: editResult.allowed, // Save requires same permission as edit
          canShare: shareResult.allowed,
          canManagePermissions: adminResult.allowed, // Use admin permission for permissions management
          canManageSettings: adminResult.allowed, // Use admin permission for settings management
        });
      } catch (error) {
        console.error("Failed to check permissions:", error);
        setPermissions({
          canEdit: false,
          canSave: false,
          canShare: false,
          canManagePermissions: false,
          canManageSettings: false,
        });
      }
    };

    checkProjectPermissions();
  }, [projectId, project, checkPermission, currentUserIds]);

  const { canEdit } = permissions;

  // Only render permission warning banner (toolbar buttons are now in EditorLayout header)
  if (canEdit) {
    return null;
  }

  return (
    <div
      className="border-b px-4 py-2"
      style={{
        backgroundColor: WARNING.bg,
        borderBottomColor: WARNING.border,
      }}
    >
      <div className="flex items-center gap-2 text-sm" style={{ color: WARNING.text }}>
        <Shield className="w-4 h-4" style={{ color: WARNING.icon }} />
        <span>
          You're viewing this project in read-only mode. Contact the project
          owner for edit access.
        </span>
      </div>
    </div>
  );
};
